/* eslint-disable prettier/prettier */
// src/modules/owner/pages/actions/tanqueCheio/main.jsx

import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Fab, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Add, BarChart, Delete, Edit, Stop, Share, Receipt, FileCopy } from '@mui/icons-material';
import MaterialGrid from '@material-ui/core/Grid';
import { isMobile } from 'react-device-detect';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import Table from 'common/components/table/table-material';
import { ActionNameElement, ColumnsBelowSearch } from 'common/components/table/tableComponents';
import { appColors } from 'styles/colors';
import TanqueCheioModal from './components/TanqueCheioModal';
import PrimaryButton from 'common/components/buttons/primaryButton';
import axios from 'axios';
import { BASE_API, USER_TOKEN, BASE_S3 } from 'config/consts';
import PostosConfigModal from './components/PostosConfigModal';
import VoucherBatchModal from './components/VoucherBatchModal';
import ShareModal from './components/ShareModal';
import ResultsModal from './components/ResultsModal';
import Reports from './components/reports';
import { getList as getLottiesList } from '../../lotties/lottiesActions';
import { getFormValues, initialize } from 'redux-form';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import LoadingProgress from 'common/components/progress/loading';
import { TANQUE_CHEIO_TYPE_ID } from './constants';

function Main(props) {
  const [open, setOpen] = useState(false);
  const [postosModalOpen, setPostosModalOpen] = useState(false);
  const [postosTabValue, setPostosTabValue] = useState(0);
  const [tablePage, setTablePage] = useState(1);
  const [shownPerPage, setShownPerPage] = useState(5);
  const [nameSearched, setNameSearched] = useState('');
  const [value, setValue] = useState(0);
  const [form, setForm] = useState(props.formValues || {});
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [resultsModalOpen, setResultsModalOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [endConfirmOpen, setEndConfirmOpen] = useState(false);
  const [endingActivityId, setEndingActivityId] = useState(null);
  const [endingLoading, setEndingLoading] = useState(false);
  const [currentView, setCurrentView] = useState('activities');

  const stationId = props.auth?.user?.establishments?.[0]?.stations?.[0]?.id;
  useEffect(() => {
    const allowedStations = [1, 148, 174, 178];
    if (stationId && !allowedStations.includes(stationId)) {
      props.router.push('/owner');
    }
  }, [stationId, props.router]);

  const handleOpen = useCallback(() => {
    setSelectedActivity(null);
    setForm({});
    setValue(0);
    props.dispatch(initialize('tanqueCheioForm', { type_id: TANQUE_CHEIO_TYPE_ID }));
    setOpen(true);
  }, [props]);
  const handleClose = useCallback(() => setOpen(false), []);
  const handlePostosModalOpen = useCallback(() => setPostosModalOpen(true), []);
  const handlePostosModalClose = useCallback(() => setPostosModalOpen(false), []);
  const handlePostosTabChange = useCallback((_event, newValue) => setPostosTabValue(newValue), []);

  const handleEditActivity = useCallback(async (activity) => {
    console.log('Editando atividade:', activity.id);
    
    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await axios.get(`${BASE_API}/actions/${activity.id}`, {
        headers: {
          Authorization: `${token}`,
        },
      });
      
      const activityData = response.data;
      
      let parsedBody = {};
      if (activityData.body) {
        try {
          parsedBody = JSON.parse(activityData.body);
        } catch (e) {
          console.warn('Erro ao fazer parse do body:', e);
        }
      }

      // Mapear categorias para o formato esperado pelo NewSelectMultiple
      const categoriesField = (activityData.category_actions || []).map(ca => ({
        id: ca.category?.id || ca.category_id,
        title: ca.category?.name || '',
      })).filter(c => c.id);

      // Mapear agrupamentos
      const groupingsField = (activityData.groupings || parsedBody.groupings || []).map(g => ({
        id: g.id || g,
        title: g.name || '',
      }));

      // Mapear stations - buscar nomes das stations se necessário
      let stationsField = (parsedBody.stations_field || []).map(s => ({
        id: s.id,
        title: s.title || s.name || '',
      }));
      if (stationsField.length === 0 && activityData.station_id) {
        stationsField = [{ id: activityData.station_id, title: activityData.station?.name || '' }];
      }
      // Buscar nomes das stations que estão sem título
      if (stationsField.some(s => !s.title)) {
        try {
          const stationsResponse = await axios.get(`${BASE_API}/stations`, {
            headers: { Authorization: `${token}` },
          });
          const stationsMap = (stationsResponse.data || []).reduce((acc, s) => {
            acc[s.id] = s.name;
            return acc;
          }, {});
          stationsField = stationsField.map(s => ({
            ...s,
            title: s.title || stationsMap[s.id] || `Station ${s.id}`,
          }));
        } catch (e) {
          console.warn('Erro ao buscar nomes das stations:', e);
        }
      }

      // Converter datas ISO para formato datetime-local (YYYY-MM-DDTHH:mm)
      const toDatetimeLocal = (isoStr) => {
        if (!isoStr) return '';
        try {
          const d = new Date(isoStr);
          if (isNaN(d.getTime())) return '';
          const pad = (n) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        } catch (e) { return ''; }
      };

      // Extrair dados dos objetos aninhados da API
      const voucherInfo = (typeof activityData.voucher_info === 'object' && activityData.voucher_info) || {};
      const fuelConfig = (typeof activityData.fuel_config === 'object' && activityData.fuel_config) || {};

      const formData = {
        id: activityData.id,
        ...activityData,
        ...parsedBody,
        // Campos que devem vir da API
        name: activityData.name || parsedBody.name,
        body_name: activityData.name,
        body_description: activityData.description || parsedBody.description,
        body_summary: activityData.summary || parsedBody.summary,
        description: activityData.description || parsedBody.description,
        summary: activityData.summary || parsedBody.summary,
        categories_field: categoriesField.length > 0 ? categoriesField : (parsedBody.categories_field || []),
        groupings_field: groupingsField.length > 0 ? groupingsField : (parsedBody.groupings_field || []),
        stations_field: stationsField,
        station_id: parsedBody.station_id || activityData.station_id,
        image: activityData.image || parsedBody.image,
        // Reconstruir station_images para exibir a imagem na edição
        station_images: parsedBody.station_images || (() => {
          const img = activityData.image || parsedBody.image;
          const sid = parsedBody.station_id || activityData.station_id;
          if (img && sid) return { [sid]: img };
          return {};
        })(),
        click_me_label: activityData.click_me_label || parsedBody.click_me_label || '',
        // Datas convertidas para formato datetime-local
        start: toDatetimeLocal(activityData.start),
        end: toDatetimeLocal(activityData.end),
        participationStart: toDatetimeLocal(activityData.participationStart),
        participationEnd: toDatetimeLocal(activityData.participationEnd),
        // Campos Tanque Cheio extraídos de voucher_info e fuel_config
        voucher_expiration: toDatetimeLocal(voucherInfo.voucher_expiration || parsedBody.voucher_expiration),
        tipo_combustivel: fuelConfig.tipo_combustivel || activityData.fuel_type || parsedBody.tipo_combustivel || '',
        rede_id: fuelConfig.rede_id || parsedBody.rede_id || '',
        voucher_regulation: voucherInfo.voucher_regulation || activityData.rules || parsedBody.voucher_regulation || '',
        liters_per_voucher: voucherInfo.liters_per_voucher || parsedBody.liters_per_voucher || '',
        vouchers_per_client: voucherInfo.vouchers_per_client || parsedBody.vouchers_per_client || '',
        net_price_per_liter: fuelConfig.net_price_per_liter || activityData.fuel_price_per_liter || parsedBody.net_price_per_liter || '',
        total_vouchers_available: voucherInfo.total_vouchers_available || parsedBody.total_vouchers_available || '',
        delivery_locations: activityData.delivery_locations || parsedBody.delivery_locations || [],
        is_draft: activityData.is_draft,
        is_tanque_cheio: activityData.is_tanque_cheio,
        // Switches da publicação e segmentação
        has_target: activityData.has_target || false,
        target: activityData.target,
        ...(() => {
          let t = activityData.target;
          if (!t) return {};
          if (typeof t === 'string') {
            try { t = JSON.parse(t); } catch (e) { return {}; }
          }
          if (t.segment_users && Array.isArray(t.segment_users) && t.segment_users.length > 0) {
            return { segmentation_type: 'users', segment_users: t.segment_users };
          }
          if (t.age || t.states || t.associated || t.internal || t.plans || t.entities) {
            return {
              segmentation_type: 'public',
              from: t.age?.from || '',
              to: t.age?.to || '',
              states: t.states || [],
              associated: t.associated || 'all',
              internal: t.internal || 'all',
              segment_plans: t.plans || [],
              entities: t.entities || [],
            };
          }
          return {};
        })(),
        only_public: activityData.only_public || false,
        featuredPrivate: activityData.featuredPrivate || false,
        featuredPublic: activityData.featuredPublic || false,
        comments_and_likes_enabled: activityData.comments_and_likes_enabled || false,
        hasPriority: activityData.hasPriority || false,
        hasPriorityInCarousel: activityData.hasPriorityInCarousel || false,
      };

      // Inicializar o redux-form com os dados da atividade
      props.dispatch(initialize('tanqueCheioForm', formData));
      
      setSelectedActivity(activity);
      setForm(formData);
      setValue(0);
      setOpen(true);
    } catch (error) {
      console.error('Erro ao carregar dados da atividade:', error);
      const fallbackData = {
        ...activity.rawData,
        name: activity.name,
        id: activity.id
      };
      
      props.dispatch(initialize('tanqueCheioForm', fallbackData));
      
      setSelectedActivity(activity);
      setForm(fallbackData);
      setValue(0);
      setOpen(true);
    }
  }, [props]);

  const handleViewResults = useCallback((activity) => {
    console.log('Ver resultados:', activity.id);
    setSelectedActivity(activity);
    setResultsModalOpen(true);
  }, []);

  const handleEndActivity = useCallback((activityId) => {
    setEndingActivityId(activityId);
    setEndConfirmOpen(true);
  }, []);

  const handleShareActivity = useCallback((activity) => {
    console.log('Divulgando atividade:', activity.id);
    setSelectedActivity(activity);
    setShareModalOpen(true);
  }, []);

  const handleGenerateVouchers = useCallback((activity) => {
    console.log('Gerando vouchers em lote:', activity.id);
    setSelectedActivity(activity);
    setVoucherModalOpen(true);
  }, []);

  // Função para buscar atividades TanqueCheio
  const fetchTanqueCheioActivities = useCallback(async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const offset = (tablePage - 1) * shownPerPage;
      const [dataResponse, totalResponse] = await Promise.all([
        axios.get(
          `${BASE_API}/actions-list-table/?where[is_tanque_cheio]=1&where[active]=1&where[limit]=${shownPerPage}&where[offset]=${offset}`,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        ),
        axios.get(
          `${BASE_API}/actions/total/?where[is_tanque_cheio]=1&where[active]=1`,
          {
            headers: {
              Authorization: `${token}`,
            },
          }
        )
      ]);

      const activities = dataResponse.data || [];
      setTotalRecords(totalResponse.data.total || 0);

      const formattedData = activities.map(activity => {
        let imageUrl = null;
        
        if (activity.image && activity.image.trim() !== '') {
          if (typeof activity.image === 'string') {
            if (activity.image.includes('http')) {
              imageUrl = activity.image;
            } else if (activity.image.startsWith('https://s3.amazonaws.com/rilato.app.files/')) {
              imageUrl = activity.image;
            } else {
              imageUrl = `https://s3.amazonaws.com/rilato.app.files/${activity.image}`;
            }
          }
        } else {
          imageUrl = null; //null para não mostrar miniatura quebrada
        }

        return {
          id: activity.id,
          name: activity.name || 'Nome não disponível',
          type: activity.fuel_type || 'Combustível',
          entity: activity.station?.name || 'N/A',
          startDate: activity.start ? new Date(activity.start).toLocaleDateString('pt-BR') : new Date(activity.createdAt).toLocaleDateString('pt-BR'),
          endDate: activity.end ? new Date(activity.end).toLocaleDateString('pt-BR') : 'Não definido',
          image: imageUrl,
          rawData: activity
        };
      });

      // Aplicar filtro de busca por nome
      const filteredData = nameSearched
        ? formattedData.filter(item =>
            typeof item.name === 'string' &&
            item.name.toLowerCase().includes(nameSearched.toLowerCase())
          )
        : formattedData;

      setData(filteredData);
    } catch (error) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [nameSearched, tablePage, shownPerPage]);

  const confirmEndActivity = useCallback(async () => {
    if (!endingActivityId) return;
    try {
      setEndingLoading(true);
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      await axios.put(
        `${BASE_API}/archive-action/${endingActivityId}`,
        {},
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );
      setEndConfirmOpen(false);
      setEndingActivityId(null);
      if (props.dispatch) {
        props.dispatch({
          type: 'SNACKBAR',
          payload: { message: 'Atividade encerrada com sucesso!', variant: 'success', open: true },
        });
      }
      fetchTanqueCheioActivities();
    } catch (error) {
      console.error('Erro ao encerrar atividade:', error);
      const errorMessage =
        error?.response?.data?.[0] ||
        error?.response?.data?.message ||
        'Erro ao encerrar atividade';
      if (props.dispatch) {
        props.dispatch({
          type: 'SNACKBAR',
          payload: { message: errorMessage, variant: 'error', open: true },
        });
      }
    } finally {
      setEndingLoading(false);
    }
  }, [endingActivityId, fetchTanqueCheioActivities, props]);

  useEffect(() => {
    props.getLottiesList();
    fetchTanqueCheioActivities();
  }, [props, fetchTanqueCheioActivities]);

  useEffect(() => {
    setForm(props.formValues || {});
  }, [props.formValues]);

  function goBack() {
    setValue(value - 1);
  }

  function handleOpenConfirmation() {
    setOpenConfirmation(true);
  }

  function handleCancelClose() {
    setOpenConfirmation(false);
  }

  function handleNativeChange(_e, newValue) {
    setValue(newValue);
  }

  const handlePageChange = useCallback((newPage) => {
    setTablePage(newPage + 1);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setShownPerPage(newRowsPerPage);
    setTablePage(1);
  }, []);

  async function handleChange(values = {}, router) {
    const payload = {
      name: values.name || values.body_name || 'Atividade Tanque Cheio',
      description: values.description || values.body_description,
      station_id: values.station_id,
      start: values.start || new Date().toISOString(),
      end: values.end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      participationStart: values.participationStart || new Date().toISOString(),
      participationEnd: values.participationEnd || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      click_me_label: values.click_me_label || 'Participar',
      limit: values.total_vouchers_available || values.limit || 1000,
      limitPerUser: values.limitPerUser || 1,
      points: values.points || 0,
      active: values.active !== undefined ? values.active : 1,
      is_draft: values.is_draft !== undefined ? values.is_draft : 1,
      image: values.image,
      rules: values.voucher_regulation || values.rules,
      has_rules: values.has_rules || (values.voucher_regulation ? true : false),
      feedback_message: values.feedback_message,
      feedback_lottie_id: values.feedback_lottie_id,
      has_target: values.has_target || false,
      target: values.target,
      audience_types: values.audience_types,
      comments_and_likes_enabled: values.comments_and_likes_enabled || false,
      trophy_image: values.trophy_image,
      trophy_description: values.trophy_description,
      summary: values.body_summary || values.summary,
      categories: values.categories,
      
      // Campos específicos do TanqueCheio
      code: values.code,
      teacher: values.teacher,
      partner_id: values.partner_id,
      local: values.local,
      price: values.net_price_per_liter || values.price || 0,
      discount_price: values.discount_price || 0,
      date: values.date || values.end,
      
      // Body com dados específicos do TanqueCheio
      body: JSON.stringify({
        name: values.name || values.body_name,
        description: values.description || values.body_description,
        station_id: values.station_id,
        station_images: values.station_images,
        liters_per_voucher: values.liters_per_voucher,
        total_vouchers_available: values.total_vouchers_available,
        vouchers_per_client: values.vouchers_per_client,
        net_price_per_liter: values.net_price_per_liter,
        tipo_combustivel: values.tipo_combustivel,
        rede_id: values.rede_id,
        voucher_regulation: values.voucher_regulation,
        voucher_expiration: values.voucher_expiration,
        fuel_config: values.fuel_config,
        voucher_info: values.voucher_info,
        ...values,
      })
    };

    try {
      const token = JSON.parse(localStorage.getItem(USER_TOKEN));
      const response = await fetch(`${BASE_API}/tanque-cheio/save-draft`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        return false;
      }
    } catch (error) {
      return false;
    }
  }

  function onDialogClose() {
    setOpenConfirmation(false);
    setOpen(false);
    setSelectedActivity(null);
    setForm(props.formValues || {});
    setTimeout(() => {
      props.router.push('/owner/tanque-cheio');
    }, 100);
  }

  const columns = isMobile
    ? [
        {
          field: 'name',
          title: 'Nome',
          render: rowData => (
            <ActionNameElement
              actionName={rowData.name}
              image={rowData.image}
            />
          ),
        },
        {
          field: 'type',
          title: 'Tipo',
          render: rowData => <ActionNameElement actionName={rowData.type} />,
        },
        {
          field: 'actions',
          title: 'Ações',
          headerCenter: true,
          hasOnClick: true,
          render: () => (
            <ColumnsBelowSearch
              buttons={{
                namedButtons: [],
                iconButtons: [{
                  name: 'edit',
                  onClick: () => {},
                  color: appColors.primary
                }],
              }}
            />
          ),
        },
      ]
    : [
        {
          field: 'name',
          title: 'Nome',
          render: rowData => (
            <ActionNameElement
              actionName={rowData.name}
              image={rowData.image}
            />
          ),
        },
        {
          field: 'type',
          title: 'Tipo',
          render: rowData => <ActionNameElement actionName={rowData.type} />,
        },
        {
          field: 'entity',
          title: 'Entidade',
          render: rowData => <ActionNameElement actionName={rowData.entity} />,
        },
        {
          field: 'startDate',
          title: 'Início da Publicação',
          render: rowData => <ActionNameElement actionName={rowData.startDate} />,
        },
        {
          field: 'endDate',
          title: 'Fim da Publicação',
          render: rowData => <ActionNameElement actionName={rowData.endDate} />,
        },
        {
          field: 'actions',
          title: 'Ações',
          headerCenter: true,
          hasOnClick: true,
          render: rowData => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => handleEditActivity(rowData)}
                  sx={{ p: 1, minWidth: 'auto' }}
                  title="Editar"
                >
                  <Edit sx={{ fontSize: 18, color: '#666' }} />
                </Button>
                <Button
                  size="small"
                  onClick={() => handleViewResults(rowData)}
                  sx={{ p: 1, minWidth: 'auto' }}
                  title="Ver Resultados"
                >
                  <BarChart sx={{ fontSize: 18, color: '#666' }} />
                </Button>
                <Button
                  size="small"
                  onClick={() => handleEndActivity(rowData.id)}
                  sx={{ p: 1, minWidth: 'auto' }}
                  title="Encerrar"
                >
                  <Stop sx={{ fontSize: 18, color: '#666' }} />
                </Button>
                <Button
                  size="small"
                  onClick={() => handleShareActivity(rowData)}
                  sx={{ p: 1, minWidth: 'auto' }}
                  title="Divulgar"
                >
                  <Share sx={{ fontSize: 18, color: '#666' }} />
                </Button>
              </Box>
              {/* Segunda linha */}
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => handleGenerateVouchers(rowData)}
                  sx={{ p: 1, minWidth: 'auto' }}
                  title="Gerar Vouchers em Lote"
                >
                  <Receipt sx={{ fontSize: 18, color: '#666' }} />
                </Button>
              </Box>
            </Box>
          ),
        },
      ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
      <GeneralGrid container spacing={3}>
        <GeneralGrid item container xs={12} justifyContent="center">
          <GeneralGrid item xs={12} style={{ width: isMobile ? '85vw' : undefined }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button
                onClick={handlePostosModalOpen}
                sx={{
                  color: '#1976d2',
                  fontWeight: 700,
                }}
              >
                Configurar postos
              </Button>
              <Button
                onClick={() => setCurrentView('reports')}
                sx={{
                  color: currentView === 'reports' ? '#1976d2' : 'inherit',
                  fontWeight: currentView === 'reports' ? 700 : 500,
                }}
              >
                Relatórios
              </Button>
              {currentView === 'reports' && (
                <Button onClick={() => setCurrentView('activities')}>
                  Voltar para atividades
                </Button>
              )}
            </Box>
            {currentView === 'activities' ? (
              <Table
                toolbarSearch
                disableSearchIconClick
                searchBarOnChange={setNameSearched}
                searchPlaceholder="Pesquisar atividade"
                title="Atividades Tanque Cheio"
                columns={columns}
                rows={data}
                dataType="tanqueCheio"
                setAPIPage={handlePageChange}
                setAPILimit={handleRowsPerPageChange}
                countFromAPI={totalRecords}
                loading={loading}
                mobileTable={isMobile}
                hasBorder={!isMobile}
                currentPage={tablePage - 1}
                rowsPerPage={shownPerPage}
                rowsPerPageOptions={[5, 10, 25]}
                defaultRowsPerPage={5}
              />
            ) : (
              <Reports embedded />
            )}
          </GeneralGrid>
        </GeneralGrid>
      </GeneralGrid>

      {currentView === 'activities' && (
        <Fab
          color="primary"
          sx={{ position: 'fixed', bottom: 100, right: 64 }}
          onClick={handleOpen}
        >
          <Add />
        </Fab>
      )}

      <TanqueCheioModal
        visible={open}
        onClose={handleOpenConfirmation}
        form={form}
        value={value}
        height="95vh"
        goBack={goBack}
        handleNativeChange={handleNativeChange}
        handleOpenConfirmation={handleOpenConfirmation}
        handleCancelClose={handleCancelClose}
        openConfirmation={openConfirmation}
        setOpenConfirmation={setOpenConfirmation}
        onClickProgress={async (values) => {
          return handleChange(values, props.router);
        }}
        router={props.router}
        location={props.location}
        closeModal={onDialogClose}
        editMode={!!selectedActivity}
        activityId={selectedActivity?.id}
      />

      <PostosConfigModal
        open={postosModalOpen}
        onClose={handlePostosModalClose}
        tabValue={postosTabValue}
        onTabChange={handlePostosTabChange}
      />

      <VoucherBatchModal
        open={voucherModalOpen}
        onClose={() => {
          setVoucherModalOpen(false);
          setSelectedActivity(null);
        }}
        activity={selectedActivity}
      />

      <ShareModal
        open={shareModalOpen}
        onClose={() => {
          setShareModalOpen(false);
          setSelectedActivity(null);
        }}
        activity={selectedActivity}
        auth={props.auth}
      />

      <ResultsModal
        open={resultsModalOpen}
        onClose={() => {
          setResultsModalOpen(false);
          setSelectedActivity(null);
        }}
        activity={selectedActivity}
      />

      <Dialog
        open={endConfirmOpen}
        onClose={() => {
          if (!endingLoading) {
            setEndConfirmOpen(false);
            setEndingActivityId(null);
          }
        }}
      >
        <DialogTitle>Encerrar Atividade</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja encerrar esta atividade? Esta ação não poderá ser desfeita facilmente.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEndConfirmOpen(false);
              setEndingActivityId(null);
            }}
            disabled={endingLoading}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmEndActivity}
            color="error"
            variant="contained"
            disabled={endingLoading}
          >
            {endingLoading ? 'Encerrando...' : 'Encerrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

Main.propTypes = {
  auth: PropTypes.object.isRequired,
  coupons: PropTypes.object,
  formValues: PropTypes.any,
  getLottiesList: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  coupons: state.coupons,
  formValues: getFormValues('tanqueCheioForm')(state),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators({ getLottiesList }, dispatch),
  dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(Main);